export function normaliseReference(value) {
  return String(value ?? '').trim().toUpperCase().replace(/\s+/g, '');
}

export function expandLookupData(data) {
  if (Array.isArray(data.applications)) return data.applications;
  if (data.format !== 'remax-application-index-v1' || !Array.isArray(data.rows)) throw new Error('Invalid application catalogue');
  return data.rows.map((r) => ({
    rowId: r[0], sourceRow: r[1], sourceRecordId: r[2],
    make: data.makes[r[3]], model: data.models[r[4]], yearLabel: data.years[r[5]],
    components: Array.from({ length: r[6].length / 2 }, (_, i) => {
      const heading = data.headings[r[6][i * 2]];
      const reference = data.references[r[6][i * 2 + 1]];
      return {
        group: heading[0], label: heading[1], referenceText: reference[0],
        codeReferences: reference[1],
        matches: reference[2].map((m) => ({ ...data.products[m[1]], code: m[0] })),
        status: reference[3], unavailableCodes: reference[4], unresolvedReferenceFragments: reference[5],
      };
    }),
  }));
}

export function buildApplicationIndex(applications) {
  return applications.map((row) => {
    const codes = new Set();
    for (const component of row.components ?? []) {
      for (const reference of component.codeReferences ?? []) {
        const value = typeof reference === 'string' ? reference : reference.code ?? reference.reference ?? '';
        const key = normaliseReference(value);
        if (key) codes.add(key);
      }
      for (const match of component.matches ?? []) {
        for (const value of [match.code, match.sku, ...(match.variantSkus ?? [])]) {
          const key = normaliseReference(value);
          if (key) codes.add(key);
        }
      }
      const literal = normaliseReference(component.referenceText);
      if (literal) codes.add(literal);
    }
    return { row, codes };
  });
}

export function filterApplications(index, filters) {
  const part = normaliseReference(filters.part);
  return index.filter(({ row, codes }) =>
    (filters.make == null || row.make === filters.make) &&
    (filters.model == null || row.model === filters.model) &&
    (filters.yearLabel == null || row.yearLabel === filters.yearLabel) &&
    (!part || codes.has(part))
  ).map(({ row }) => row);
}

export function applicationChoices(applications, field, filters = {}) {
  const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
  return [...new Set(applications.filter((row) =>
    (filters.make == null || row.make === filters.make) &&
    (filters.model == null || row.model === filters.model)
  ).map((row) => String(row[field] ?? '')))].sort(collator.compare);
}

const ElementBase = globalThis.HTMLElement ?? class {};

class RemaxApplicationFinder extends ElementBase {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;
    this.translations = JSON.parse(this.querySelector('[data-translations]').textContent);
    this.form = this.querySelector('form');
    this.controls = this.querySelector('[data-controls]');
    this.make = this.querySelector('[data-make]');
    this.model = this.querySelector('[data-model]');
    this.year = this.querySelector('[data-year]');
    this.partInput = this.querySelector('[data-part]');
    this.status = this.querySelector('[data-status]');
    this.summary = this.querySelector('[data-summary]');
    this.results = this.querySelector('[data-results]');
    this.more = this.querySelector('[data-more]');
    this.retry = this.querySelector('[data-retry]');
    this.pageSize = 10;
    this.form.addEventListener('submit', (event) => { event.preventDefault(); this.renderResults(); });
    this.make.addEventListener('change', () => { this.updateModels(); this.updateYears(); this.renderResults(); });
    this.model.addEventListener('change', () => { this.updateYears(); this.renderResults(); });
    this.year.addEventListener('change', () => this.renderResults());
    this.querySelector('[data-reset]').addEventListener('click', () => {
      this.make.value = ''; this.partInput.value = ''; this.updateModels(); this.updateYears(); this.renderResults();
    });
    this.more.addEventListener('click', () => this.appendResults());
    this.retry.addEventListener('click', () => this.load());
    this.load();
  }

  t(key, values = {}) {
    let result = this.translations[key] ?? key;
    for (const [name, value] of Object.entries(values)) result = result.replaceAll(`[[${name}]]`, String(value));
    return result;
  }

  async load() {
    this.controls.disabled = true;
    this.retry.hidden = true;
    this.status.textContent = this.t('loading');
    try {
      const response = await fetch(this.dataset.source, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Catalogue unavailable');
      this.applications = expandLookupData(await response.json());
      this.index = buildApplicationIndex(this.applications);
      const makes = applicationChoices(this.applications, 'make');
      this.fillSelect(this.make, makes, this.t('all_makes'));
      this.updateModels(); this.updateYears();
      this.controls.disabled = false;
      this.summary.textContent = this.t('loaded', {
        entries: this.applications.length.toLocaleString('en-GB'), makes: makes.length.toLocaleString('en-GB'),
      });
      const params = new URLSearchParams(window.location.search);
      this.partInput.value = params.get('part') ?? '';
      if (params.has('make')) this.make.value = `v:${params.get('make')}`;
      this.updateModels();
      if (params.has('model')) this.model.value = `v:${params.get('model')}`;
      this.updateYears();
      if (params.has('year')) this.year.value = `v:${params.get('year')}`;
      this.renderResults();
    } catch {
      this.status.textContent = this.t('error');
      this.retry.hidden = false;
    }
  }

  selected(select) {
    return select.value.startsWith('v:') ? select.value.slice(2) : null;
  }

  fillSelect(select, choices, placeholder) {
    select.replaceChildren(new Option(placeholder, ''));
    for (const choice of choices) select.add(new Option(choice || this.t('unspecified'), `v:${choice}`));
  }

  updateModels() {
    const make = this.selected(this.make);
    this.model.disabled = make == null;
    this.fillSelect(this.model, make == null ? [] : applicationChoices(this.applications, 'model', { make }), this.t(make == null ? 'choose_make' : 'all_models'));
  }

  updateYears() {
    const make = this.selected(this.make);
    const model = this.selected(this.model);
    this.year.disabled = model == null;
    this.fillSelect(this.year, model == null ? [] : applicationChoices(this.applications, 'yearLabel', { make, model }), this.t(model == null ? 'choose_model' : 'all_years'));
  }

  renderResults() {
    if (!this.index) return;
    this.results.replaceChildren();
    this.more.hidden = true;
    this.shown = 0;
    const filters = {
      make: this.selected(this.make), model: this.selected(this.model),
      yearLabel: this.selected(this.year), part: this.partInput.value,
    };
    if (filters.make == null && !normaliseReference(filters.part)) {
      this.status.textContent = this.t('start');
      return;
    }
    this.filtered = filterApplications(this.index, filters);
    if (!this.filtered.length) { this.status.textContent = this.t('no_results'); return; }
    this.appendResults();
  }

  appendResults() {
    const next = this.filtered.slice(this.shown, this.shown + this.pageSize);
    const fragment = document.createDocumentFragment();
    for (const row of next) fragment.append(this.renderRow(row));
    this.results.append(fragment);
    this.shown += next.length;
    this.status.textContent = this.t('count', {
      shown: this.shown.toLocaleString('en-GB'), total: this.filtered.length.toLocaleString('en-GB'),
    });
    this.more.hidden = this.shown >= this.filtered.length;
  }

  el(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text != null) element.textContent = String(text);
    return element;
  }

  renderRow(row) {
    const article = this.el('article', 'rm-fitment');
    const header = this.el('div', 'rm-fitment-heading');
    header.append(this.el('h2', '', [row.make, row.model].filter(Boolean).join(' · ')));
    header.append(this.el('p', 'rm-fitment-year', `${this.t('year')}: ${row.yearLabel || this.t('unspecified')}`));
    header.append(this.el('span', 'rm-fitment-source', this.t('source', { row: row.sourceRow ?? row.rowId })));
    article.append(header);
    const components = (row.components ?? []).filter((c) => String(c.referenceText ?? '').trim());
    if (!components.length) { article.append(this.el('p', 'rm-finder-scope', this.t('no_components'))); return article; }
    const table = this.el('table', 'rm-fitment-table');
    const tableHead = this.el('thead');
    const titleRow = this.el('tr');
    for (const key of ['component', 'reference', 'matches']) {
      const th = this.el('th', '', this.t(key)); th.scope = 'col'; titleRow.append(th);
    }
    tableHead.append(titleRow); table.append(tableHead);
    const tbody = this.el('tbody');
    for (const component of components) {
      const tr = this.el('tr');
      const label = this.el('td', 'rm-fitment-component');
      label.append(this.el('strong', '', component.label));
      if (component.group) label.append(this.el('small', '', component.group));
      const reference = this.el('td', '', component.referenceText);
      reference.dataset.label = this.t('reference');
      const matched = this.el('td'); matched.dataset.label = this.t('matches');
      if (component.matches?.length) {
        const list = this.el('ul', 'rm-fitment-matches');
        const seen = new Set();
        for (const match of component.matches) {
          const key = `${match.productHandle}|${match.sku}|${match.code}`;
          if (seen.has(key)) continue;
          seen.add(key);
          const li = this.el('li');
          const clickable = this.dataset.productLinks === 'true' && match.productHandle;
          const part = this.el('a', 'rm-fitment-part');
          part.href = clickable ? `${this.dataset.rootUrl.replace(/\/$/, '')}/products/${encodeURIComponent(match.productHandle)}` : `/pages/reference-lookup?q=${encodeURIComponent(match.sku || match.code)}`;
          part.append(this.el('strong', '', match.code || match.sku));
          if (match.title) part.append(this.el('small', '', match.title));
          li.append(part);
          if (!clickable) li.append(this.el('span', 'rm-fitment-muted', this.t('preparing')));
          list.append(li);
        }
        matched.append(list);
      } else matched.append(this.el('span', 'rm-fitment-muted', this.t(component.status === 'source_marker' ? 'source_marker' : 'no_match')));
      if (component.matches?.length && component.unavailableCodes?.length) matched.append(this.el('p', 'rm-fitment-muted', this.t('partial_match', { codes: component.unavailableCodes.join(', ') })));
      if (component.unresolvedReferenceFragments?.length) matched.append(this.el('p', 'rm-fitment-muted', this.t('unresolved', { references: component.unresolvedReferenceFragments.join(', ') })));
      tr.append(label, reference, matched); tbody.append(tr);
    }
    table.append(tbody); article.append(table); return article;
  }
}

if (globalThis.customElements && !customElements.get('remax-application-finder')) {
  customElements.define('remax-application-finder', RemaxApplicationFinder);
}

class RemaxVehicleNavigation extends ElementBase {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;
    this.translations = JSON.parse(this.querySelector('[data-translations]').textContent);
    this.make = this.querySelector('[data-make]');
    this.model = this.querySelector('[data-model]');
    this.year = this.querySelector('[data-year]');
    this.controls = this.querySelector('[data-controls]');
    this.status = this.querySelector('[data-status]');
    this.retry = this.querySelector('[data-retry]');
    this.make.addEventListener('change', () => { this.updateModels(); this.updateYears(); });
    this.model.addEventListener('change', () => this.updateYears());
    this.retry.addEventListener('click', () => this.load());
    this.querySelector('form').addEventListener('submit', (event) => {
      event.preventDefault();
      const params = new URLSearchParams();
      for (const [name, select] of [['make', this.make], ['model', this.model], ['year', this.year]]) {
        const selected = this.selected(select);
        if (selected != null && !select.disabled) params.set(name, selected);
      }
      window.location.assign(`/pages/application-lookup?${params.toString()}`);
    });
    if ('IntersectionObserver' in globalThis) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
      }, { rootMargin: '150px' });
      this.observer.observe(this);
    } else this.load();
  }

  disconnectedCallback() { this.observer?.disconnect(); }
  t(key) { return this.translations[key] ?? key; }
  selected(select) { return select.value.startsWith('v:') ? select.value.slice(2) : null; }
  fill(select, values, placeholder) {
    select.replaceChildren(new Option(placeholder, ''));
    for (const value of values) select.add(new Option(value || this.t('unspecified'), `v:${value}`));
  }
  updateModels() {
    const make = this.selected(this.make);
    this.model.disabled = make == null;
    this.fill(this.model, make == null ? [] : applicationChoices(this.applications, 'model', { make }), this.t(make == null ? 'choose_make' : 'all_models'));
  }
  updateYears() {
    const make = this.selected(this.make);
    const model = this.selected(this.model);
    this.year.disabled = model == null;
    this.fill(this.year, model == null ? [] : applicationChoices(this.applications, 'yearLabel', { make, model }), this.t(model == null ? 'choose_model' : 'all_years'));
  }
  async load() {
    this.retry.hidden = true;
    this.controls.disabled = true;
    this.status.textContent = this.t('loading');
    try {
      const response = await fetch(this.dataset.source, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Catalogue unavailable');
      this.applications = expandLookupData(await response.json());
      this.fill(this.make, applicationChoices(this.applications, 'make'), this.t('all_makes'));
      this.updateModels(); this.updateYears();
      this.controls.disabled = false;
      this.status.textContent = this.t('ready');
    } catch {
      this.status.textContent = this.t('error');
      this.retry.hidden = false;
    }
  }
}

if (globalThis.customElements && !customElements.get('remax-vehicle-navigation')) {
  customElements.define('remax-vehicle-navigation', RemaxVehicleNavigation);
}
